import "../_namespaces.js";
import "#/shared/api/api-host-global.js";
import "../../../../../../lib/assets/javascripts/assert.js";
import "../../../utilities/ordered_ajax.js";
import "../../../utilities/recaptcha.js";
import "#vendor/libphonenumber.js";
import "./step.js";
// Step subclasses — must come AFTER step.js so that
// AppointmentBooking.Wizard.Views.Step is defined before subclasses extend it.
// Previously globbed inside step.js which caused a circular dependency.
// Uses **/ to include nested callouts, fields, and modals subdirectories.
import.meta.glob("./steps/**/*.{js,hbs,jst.ejs}", { eager: true });
import "./header.js";
import "./progress.js";
import "./navigation.js";
import "./loading_indicator.js";
import "../../../shared/accessibility.js";
import "virtual:client-templates/app/assets/javascripts/appointment_booking/wizard/views/templates";

window.AppointmentBooking ??= {};
window.AppointmentBooking.Wizard ??= {};
window.AppointmentBooking.Wizard.Views ??= {};

AppointmentBooking.Wizard.Views.Wizard = class Wizard extends AppointmentBooking.Wizard.Views.Base {
  constructor() {
    super(...arguments);
    this.recoverableErrorTypes = this.recoverableErrorTypes.bind(this);
    this.initialize = this.initialize.bind(this);
    this.render = this.render.bind(this);
    this.contextForRender = this.contextForRender.bind(this);
    this.handleProgressChange = this.handleProgressChange.bind(this);
    this.handleBookingChanged = this.handleBookingChanged.bind(this);
    this.handleSlotIdChanged = this.handleSlotIdChanged.bind(this);
    this.handleValidatedSlotIdChanged = this.handleValidatedSlotIdChanged.bind(this);
    this.handleErrorsChanged = this.handleErrorsChanged.bind(this);
    this.navigateTo = this.navigateTo.bind(this);
    this.start = this.start.bind(this);
    this.continue = this.continue.bind(this);
    this.back = this.back.bind(this);
    this.complete = this.complete.bind(this);
    this.autoContinue = this.autoContinue.bind(this);
    this.updateNavigation = this.updateNavigation.bind(this);
    this.isAtStart = this.isAtStart.bind(this);
    this.isAtEnd = this.isAtEnd.bind(this);
    this.isComplete = this.isComplete.bind(this);
    this.continueFrom = this.continueFrom.bind(this);
    this.backFrom = this.backFrom.bind(this);
    this.autoContinueFrom = this.autoContinueFrom.bind(this);
    this.showStep = this.showStep.bind(this);
    this.updateLeaveContainer = this.updateLeaveContainer.bind(this);
    this.updateEnterContainer = this.updateEnterContainer.bind(this);
    this.makeCurrentStep = this.makeCurrentStep.bind(this);
    this.updateSlotMetadata = this.updateSlotMetadata.bind(this);
    this.getCurrentStep = this.getCurrentStep.bind(this);
    this.getStep = this.getStep.bind(this);
    this.getStepIndex = this.getStepIndex.bind(this);
    this.focusStartOfStep = this.focusStartOfStep.bind(this);
    this.buildSteps = this.buildSteps.bind(this);
    this.updateStepList = this.updateStepList.bind(this);
    this.validatePrerequisites = this.validatePrerequisites.bind(this);
    this.validateRequirements = this.validateRequirements.bind(this);
    this.performReset = this.performReset.bind(this);
    this.prepareDataForSubmission = this.prepareDataForSubmission.bind(this);
    this.waitForBookingUpdate = this.waitForBookingUpdate.bind(this);
    this.isWaitingForModelUpdate = this.isWaitingForModelUpdate.bind(this);
    this.withLoadingIndicator = this.withLoadingIndicator.bind(this);
    this.showLoadingIndicator = this.showLoadingIndicator.bind(this);
    this.hideLoadingIndicator = this.hideLoadingIndicator.bind(this);
    this.setPageTitle = this.setPageTitle.bind(this);
    this.unhandledErrors = this.unhandledErrors.bind(this);
    this.bookingErrors = this.bookingErrors.bind(this);
  }

  recoverableErrorTypes() {
    return _.flatten(_.map(this.steps, (step) => step.getRecoverableErrorTypes()));
  }

  initialize() {
    this.allSteps = this.buildSteps(this.model.get("steps"));
    this.steps = [...Array.from(this.allSteps)]; // The list of unskippable steps (updated dynamically)
    this.assert.condition(this.steps.length, "Appointment Wizard has no steps");

    this.header = new AppointmentBooking.Wizard.Views.Header({ model: this.model });
    this.progress = new AppointmentBooking.Wizard.Views.Progress({ model: this.model });
    this.navigation = new AppointmentBooking.Wizard.Views.Navigation({ model: this.model });
    this.loadingIndicator = new AppointmentBooking.Wizard.Views.LoadingIndicator({ delay: 150, min: 500 });
    this.startStepElement = jQuery("<div>", { class: "start-of-step", tabindex: 0 }); // Create an element indicating the start of the step content.

    // BEHAVIOUR

    this.listenTo(this.model, "change:currentStepIndex change:completed", this.handleProgressChange);
    this.listenTo(
      this.model.get("booking"),
      "change relational:change:visit_type_rule_selections relational:change:verify_id_answers",
      this.handleBookingChanged,
    );
    this.listenTo(this.model.get("booking"), "change:ehr_appointment_slot_id", this.handleSlotIdChanged);
    this.listenTo(
      this.model.get("booking"),
      "change:validated_ehr_appointment_slot_id",
      this.handleValidatedSlotIdChanged,
    );
    this.listenTo(this.model.get("booking"), "change:errors", this.handleErrorsChanged);

    this.updateSlotMetadata("ehr_appointment_slot_id");
    if (AppointmentBooking.config.recaptcha_enabled) {
      Sparkle.Recaptcha.startSession();
    }
    return window.Sparkle.Accessibility.clickableWithKeyboard(this.$el);
  }

  render() {
    super.render();
    this.replaceContents(".header-container", this.header.render().el);
    this.replaceContents(".progress-container", this.progress.render().el);
    this.replaceContents(".navigation-container", this.navigation.render().el);
    this.$el.append(this.loadingIndicator.render().el);
    this.leaveStepContainerEl = this.$el.find(".wizard-form-container.--leave");
    this.enterStepContainerEl = this.$el.find(".wizard-form-container.--enter");

    this.updateNavigation();
    this.showStep(this.getCurrentStep());
    return this;
  }

  contextForRender() {
    return {
      ...super.contextForRender(...arguments),
      recaptchaEnabled: AppointmentBooking.config.recaptcha_enabled,
    };
  }

  // EVENT HANDLERS

  handleNavigationBackButtonClick(event) {
    // console.debug('back button pressed')
    if (!this.isAtStart() && !this.isComplete()) {
      return this.back();
    }
  }

  handleNavigationContinueButtonClick(event) {
    // TODO: Ensure no duplicate button presses
    // console.debug('continue button pressed')
    if (this.isAtEnd()) {
      return this.complete();
    } else {
      return this.continue();
    }
  }

  handleNavigationContinueButtonClickWhileDisabled(event) {
    if (!this.model.get("completed")) {
      this.getCurrentStep().showValidity();
      return this.getCurrentStep().scrollToInvalidInput();
    }
  }

  handleProgressChange() {
    this.updateNavigation();
    return this.scrollTo(this.el);
  }

  handleBookingChanged() {
    this.updateStepList();
    this.updateNavigation();
    if (this.getCurrentStep().autoContinue()) {
      return this.autoContinue();
    }
  }

  handleSlotIdChanged() {
    return this.updateSlotMetadata("ehr_appointment_slot_id");
  }

  handleValidatedSlotIdChanged() {
    return this.updateSlotMetadata("validated_ehr_appointment_slot_id", "validated_");
  }

  handleErrorsChanged() {
    if (!this.bookingErrors().length) {
      return;
    }
    if (this.unhandledErrors().length) {
      this.assert.failAssertion(`Unhandled Errors during booking: ${this.unhandledErrors().join(", ")}`);
    }

    const stepToRecoverErrors = _.find(this.steps, (step) => step.hasRecoverableErrors());
    if (stepToRecoverErrors != null) {
      return this.makeCurrentStep(stepToRecoverErrors);
    } else {
      return this.assert.failAssertion(`No step was found to recover from errors: ${this.bookingErrors().join(", ")}`); // defensive programming. We should never get here.
    }
  }

  handleSetCurrentStepProgress(event, progress) {
    return this.model.set("currentStepProgress", progress);
  }

  handlejumpToStepClick(event, stepIndex) {
    if (stepIndex < this.model.get("currentStepIndex")) {
      return this.makeCurrentStep(this.getStep(stepIndex));
    }
  }

  handleSubviewLoading() {
    return this.showLoadingIndicator();
  }

  handleSubviewLoaded() {
    return this.hideLoadingIndicator();
  }

  // DSL

  navigateTo(targetIndex) {
    if (targetIndex < this.model.get("currentStepIndex")) {
      return this.back().then(() => this.navigateTo(targetIndex));
    }
  }

  start() {
    return this.makeCurrentStep(this.getStep(0));
  }

  continue() {
    return this.continueFrom(this.model.get("currentStepIndex"));
  }

  back() {
    return this.backFrom(this.model.get("currentStepIndex"));
  }

  // PRIVATE

  complete() {
    this.model.set("completed", true); // No going back now because we're blowing away important elements
    return this.withLoadingIndicator(() => {
      // Only resolve the promise if we fail because we want the loading indicator to stay while we redirect to the confirmation page on success
      const deferred = new jQuery.Deferred();

      this.prepareDataForSubmission().then((submissionData) =>
        jQuery
          .post(this.createUrl, submissionData)
          .then((response) => this.trigger("complete", response.confirmation_url))
          .fail((jqXHR) => {
            if (AppointmentBooking.config.recaptcha_enabled) {
              Sparkle.Recaptcha.startSession();
            } // Start another recaptcha session so we can prepare the data for submission on our next attempt
            deferred.reject();

            if (jqXHR.status === 500) {
              return this.assert.failAssertion("AppointmentBookingWizard appointment creation failed with a 500 error");
            } else if (!jqXHR.responseText) {
              // We could be blocked by a CORS issue or other browser-level error
              return this.assert.failAssertion(
                `AppointmentBookingWizard appointment creation failed but no jqXHR.responseText was available on failure with status ${jqXHR.status}`,
              );
            } else {
              let response = null;
              try {
                // Need to explicitly parse responseText because jQuery 1.x does not populate responseJSON for non-200 responses
                response = JSON.parse(jqXHR.responseText);
              } catch (error) {
                this.assert.failAssertion(
                  `AppointmentBookingWizard appointment creation failed but could not parse jqXHR.responseText: ${jqXHR.responseText}`,
                );
              }

              this.model.set("completed", false);
              return this.model.get("booking").set({
                errors: response.errors,
                error_messages: response.error_messages,
                // Store the patient because there's a good chance the user will try again and we don't want to create a new patient if they do
                patient_id: response.patient_id,
                patient_id_type: response.patient_id_type,
              });
            }
          }),
      );

      return deferred.promise();
    });
  }

  autoContinue() {
    return this.autoContinueFrom(this.model.get("currentStepIndex"));
  }

  updateNavigation() {
    const step = this.getCurrentStep();

    // Button text
    if (this.model.isAtEnd()) {
      this.navigation.setButtonText("continue", this.I18n().navigation.finish_booking);
    } else {
      this.navigation.setButtonText("continue", this.I18n().navigation.continue);
    }

    // Continue button visibility
    if (step.autoContinue()) {
      this.navigation.hideButton("continue"); // If we autoContinue on this step, we don't need to show navigation since completing the step moves us on
    } else {
      this.navigation.showButton("continue");
    }

    // Back button visibility
    if (step.autoContinue() && this.isAtStart()) {
      this.navigation.hideButton("back");
    } else {
      this.navigation.showButton("back");
    }

    this.navigation.$el.toggleClass("autocontinue", step.autoContinue());

    if (this.isComplete()) {
      return this.navigation.disable();
    } else {
      this.navigation.toggleButton("back", !this.isAtStart());
      this.navigation.holdButton("continue");
      return this.validateRequirements(step)
        .then(() => this.navigation.resumeButton("continue"))
        .fail(() => this.navigation.resumeButton("continue", false));
    }
  }

  isAtStart() {
    return this.model.isAtStart();
  }

  isAtEnd() {
    return this.model.isAtEnd();
  }

  isComplete() {
    return this.model.get("completed");
  }

  // Move to the next step if all the step prerequisites are met, show a loading indicator while we wait
  // for the data model to update, at which point we try show again.
  continueFrom(index) {
    const step = this.getStep(index);
    return this.validateRequirements(step)
      .then(() =>
        // console.debug('requirements met', step)
        this.showStep(this.getStep(index + 1)),
      )
      .fail(() =>
        // console.debug('requirements not met', step)
        this.waitForBookingUpdate().then(() => this.continueFrom(index)),
      );
  }

  // Going back does not check prerequisites or requirements
  backFrom(index) {
    return this.performReset(this.getStep(index)).then(() => {
      const previousStep = this.getStep(index - 1);

      if (previousStep.isSkippable()) {
        return this.backFrom(index - 1);
      } else {
        return this.makeCurrentStep(previousStep);
      }
    });
  }

  // Attempt to continue from the current step, but if requirements are not met we abandon the attempt
  autoContinueFrom(index) {
    const step = this.getStep(index);
    return this.validateRequirements(step).then(() => this.continueFrom(index));
  }

  showStep(step) {
    return this.validatePrerequisites(step).then(() => {
      if (step.isSkippable()) {
        return this.showStep(this.getStep(this.getStepIndex(step) + 1));
      } else {
        return this.makeCurrentStep(step);
      }
    });
  }

  updateLeaveContainer(enterIndex, leaveIndex) {
    if (enterIndex !== leaveIndex) {
      const currentStepHtml = this.enterStepContainerEl.html();
      return this.leaveStepContainerEl.empty().html(currentStepHtml).show();
    }
  }

  updateEnterContainer(step) {
    this.enterStepContainerEl.hide();

    const stepContainer = this.enterStepContainerEl.find(".step-container");
    this.replaceContents(stepContainer, step.render().el); // We render the step before showing to ensure it is rendered. This allows us to hold off on rendering a step until its prerequisites have been met

    return this.leaveStepContainerEl.fadeOut("fast", () => {
      this.enterStepContainerEl.fadeIn();
      return this.leaveStepContainerEl.empty();
    });
  }

  makeCurrentStep(step) {
    const index = this.getStepIndex(step);
    this.updateLeaveContainer(index, this.getStepIndex(this.getCurrentStep()));
    this.getCurrentStep().setRenderable(false);
    step.setRenderable(true); // Allow the new step to render
    if (typeof step.beforeShow === "function") {
      step.beforeShow();
    }
    this.setPageTitle(step.getPageTitle());
    this.updateEnterContainer(step);
    this.focusStartOfStep();
    this.model.set({ currentStepIndex: index, currentStepProgress: 0 });
    return typeof step.afterShow === "function" ? step.afterShow() : undefined;
  }

  updateSlotMetadata(responseSlotIdAttribute, prefix) {
    const booking = this.model.get("booking");
    const slotId = booking.get(responseSlotIdAttribute);

    if (!slotId) {
      return;
    }

    return this.withLoadingIndicator(() =>
      Sparkle.Utilities.OrderedAJAX.getJSON(
        "AppointmentBookingWizard:slot",
        this.slotMetadataUrl.replace("%{slotId}", slotId),
      )
        .then((json) => {
          if (slotId !== booking.get(responseSlotIdAttribute)) {
            return;
          }
          return booking.setWithPrefix(json, prefix);
        })
        .fail((jqXHR) => {
          if (jqXHR.status === 404) {
            return;
          }
          return this.assert.failAssertion("Error fetching appointment slot metadata for slot", {
            description: `slot_id: '${slotId}'`,
          });
        }),
    );
  }

  getCurrentStep() {
    return this.getStep(this.model.get("currentStepIndex"));
  }

  getStep(index) {
    return this.assert.presence(this.steps[index], `No Step at index ${index}`);
  }

  getStepIndex(step) {
    const index = this.steps.indexOf(step);
    this.assert.condition(index > -1, `Could not find index of step ${step.title}.`);
    return index;
  }

  focusStartOfStep() {
    this.startStepElement.insertBefore(this.$(".step-container"));
    return this.startStepElement[0].focus(); // Focus on the element indicating the start of the step so that dynamically replaced content is targeted when tabbing. Using vanilla JS for focus because jquery did not focus the element.
  }

  // Accepts an array of step names and instantiates the corresponding steps, returning a new array contain them
  buildSteps(lookupNames) {
    return lookupNames.map((lookupName) => {
      const stepClass = AppointmentBooking.Wizard.Views.Step.lookup(lookupName);
      const step = new stepClass({ model: this.model.get("booking") });
      step.lookupName = lookupName; // Record the lookup name so when we update the step list we can update the model's steps too
      step.setRenderable(false); // We'll enable rendering of steps when necessary, but we don't want them to render when the model is updated if they're not visible
      return step;
    });
  }

  // Determine which steps are skippable and update the list of steps
  updateStepList() {
    const currentStep = this.getCurrentStep();
    const stepLookupNames = [];
    const steps = [];

    this.allSteps.forEach((step) => {
      if (step.isDisabled()) {
        return;
      }
      stepLookupNames.push(step.lookupName);
      return steps.push(step);
    });

    this.steps = steps; // Update the step views list
    this.model.set("steps", stepLookupNames); // Update the model so views know what steps are available

    if (currentStep.isDisabled()) {
      return this.makeCurrentStep(this.steps[0]); // If the current step is disabled, move to the first available step
    }
  }

  validatePrerequisites(step) {
    // console.debug('validating prerequisites', step)
    return this.withLoadingIndicator(step.validatePrerequisites);
  }

  validateRequirements(step) {
    // console.debug('validating requirements', step)
    return this.withLoadingIndicator(step.validateRequirements);
  }

  performReset(step) {
    step.setRenderable(false);
    return this.withLoadingIndicator(step.performReset);
  }

  prepareDataForSubmission() {
    const deferred = new jQuery.Deferred();
    const data = this.model.get("booking").toCreationJSON();

    if (AppointmentBooking.config.recaptcha_enabled) {
      Sparkle.Recaptcha.endSession().then((recaptchaValidationToken) => {
        data["g-recaptcha-response"] = recaptchaValidationToken;
        return deferred.resolve(data);
      });
    } else {
      deferred.resolve(data);
    }

    return deferred.promise();
  }

  // Show the loading indicator until the responses are updated by a pending ajax request, then resolve the promise. Maybe fail after a certain amount of time
  // TODO: Should this be more general, e.g. `waitForPrerequisites`? What if an ajax response does not trigger a model update because the values are unchanged?
  waitForBookingUpdate(timeout = 10000) {
    if (!this.isWaitingForModelUpdate()) {
      // console.debug('wait for model update')

      const timeoutId = setTimeout(() => {
        this.modelUpdatePromise.reject();
        // console.debug('wait for model update expired')
        return this.assert.failAssertion("Timeout waiting for booking to update");
      }, timeout);

      this.modelUpdatePromise = new jQuery.Deferred();
      this.listenToOnce(this.model.get("booking"), "change", () => {
        // console.debug('wait for model update resolved')
        clearTimeout(timeoutId);
        return this.modelUpdatePromise.resolve();
      });
    }

    return this.withLoadingIndicator(() => this.modelUpdatePromise);
  }

  isWaitingForModelUpdate() {
    return this.modelUpdatePromise && this.modelUpdatePromise.state() === "pending";
  }

  // Executes the function which returns a promise, and shows a loading indicator until that promise resolves.
  withLoadingIndicator(promiseFunction) {
    this.showLoadingIndicator();
    const promise = promiseFunction();
    promise.always(() => this.hideLoadingIndicator());
    return promise;
  }

  showLoadingIndicator() {
    return this.loadingIndicator.show();
  }

  hideLoadingIndicator() {
    return this.loadingIndicator.hide();
  }

  setPageTitle(title) {
    return (window.document.title = title);
  }

  unhandledErrors() {
    return _.difference(this.bookingErrors(), this.recoverableErrorTypes());
  }

  bookingErrors() {
    return this.model.get("booking").get("errors") || [];
  }
}; // Syntactic sugar
AppointmentBooking.Wizard.Views.Wizard.prototype.tagName = "div";
AppointmentBooking.Wizard.Views.Wizard.prototype.className = "appointment-booking-wizard";
AppointmentBooking.Wizard.Views.Wizard.prototype.template = "wizard";

AppointmentBooking.Wizard.Views.Wizard.prototype.createUrl = window.Sparkle.apiHost
  ? window.Sparkle.apiHost + window.SparkleConfig.routes.embed.ehrAppointments
  : undefined;

AppointmentBooking.Wizard.Views.Wizard.prototype.slotMetadataUrl = window.Sparkle.apiHost
  ? window.Sparkle.apiHost + window.SparkleConfig.routes.embed.ehrAppointmentSlot.replace("SLOT_ID", "%{slotId}")
  : undefined;

AppointmentBooking.Wizard.Views.Wizard.prototype.leaveStepContainerEl = null;
AppointmentBooking.Wizard.Views.Wizard.prototype.enterStepContainerEl = null;

AppointmentBooking.Wizard.Views.Wizard.prototype.events = {
  [AppointmentBooking.Wizard.Events.Navigation.backButtonClicked]: "handleNavigationBackButtonClick",
  [AppointmentBooking.Wizard.Events.Navigation.continueButtonClicked]: "handleNavigationContinueButtonClick",
  [AppointmentBooking.Wizard.Events.Navigation.continueButtonClickedWhileDisabled]:
    "handleNavigationContinueButtonClickWhileDisabled",
  [AppointmentBooking.Wizard.Events.Progress.setCurrentStepProgress]: "handleSetCurrentStepProgress",
  [AppointmentBooking.Wizard.Events.Progress.jumpToStepClicked]: "handlejumpToStepClick",
  [AppointmentBooking.Wizard.Events.AJAX.loading]: "handleSubviewLoading",
  [AppointmentBooking.Wizard.Events.AJAX.loaded]: "handleSubviewLoaded",
};

AppointmentBooking.Wizard.Views.Wizard.prototype.when = jQuery.when;
